import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  Paper,
  Autocomplete,
} from '@mui/material';
import {
  InfoOutlined,
  TrendingDown,
  TrendingUp,
  Warning,
  Article,
  Language,
} from '@mui/icons-material';
import {
  fetchAnomalies,
  fetchAnomalyStats,
  acknowledgeAnomaly,
  resolveAnomaly,
  type Anomaly,
  type AnomalyStatsResponse,
} from '../services/analytics.service';
import AnomalyDetailDrawer from '../components/anomaly/AnomalyDetailDrawer';
import CriticalAlertsBanner from '../components/anomaly/CriticalAlertsBanner';
import { usePagesList } from '../hooks/usePagesList';
import { useSiteStore } from '../store/site.store';

// Helper function to get anomaly advice
function getAnomalyAdvice(metric: string, delta_percent: number) {
  const isNegative = delta_percent < 0;
  const absChange = Math.abs(delta_percent);
  
  const advice: Record<string, { negative: string, positive: string }> = {
    clicks: {
      negative: absChange > 50 
        ? "🚨 Critical drop! Check: 1) Google Search Console for manual actions, 2) Site uptime/errors, 3) Recent content changes, 4) Competitor SERP movements"
        : "⚠️ Investigate: Review recent page changes, check for technical issues (redirects, 404s, robots.txt), analyze competitor rankings",
      positive: absChange > 50
        ? "🎉 Excellent growth! Document what's working: new content strategy, backlinks, SERP feature wins, or seasonal trends"
        : "✅ Good progress! Monitor sustainability and identify replicable strategies for other pages"
    },
    impressions: {
      negative: absChange > 50
        ? "🚨 Severe visibility loss! Check: 1) GSC Index Coverage for deindexing, 2) Keywords rankings, 3) Algorithm updates, 4) Canonicalization issues"
        : "⚠️ Review: Check if keywords are losing rankings, verify indexation status, analyze search trends for declining queries",
      positive: absChange > 50
        ? "🎉 Major visibility gain! Identify: new ranking keywords, expanded featured snippets, successful content optimization"
        : "✅ Growing visibility! Track which queries are improving and expand related content"
    },
    ctr: {
      negative: absChange > 20
        ? "🚨 Low CTR hurting performance! Improve: 1) Title tags (add power words, numbers), 2) Meta descriptions (clear value prop), 3) Schema markup for rich snippets"
        : "⚠️ CTR declining: A/B test different title formats, review meta descriptions, check if competitors have better snippets",
      positive: absChange > 20
        ? "🎉 Excellent title/snippet performance! Document winning formulas: title patterns, description hooks, schema usage"
        : "✅ CTR improving! Continue refining titles and descriptions based on what's working"
    },
    position: {
      negative: absChange > 10
        ? "🚨 Rankings dropping! Act fast: 1) Update content freshness, 2) Add expert insights/stats, 3) Build quality backlinks, 4) Improve E-E-A-T signals"
        : "⚠️ Position slipping: Analyze top-ranking competitors' content, enhance depth/quality, update outdated information",
      positive: absChange > 10
        ? "🎉 Strong ranking gains! Reinforce with: fresh content updates, more internal links, additional expert quotes/data"
        : "✅ Moving up rankings! Keep optimizing and monitor for stability"
    }
  };
  
  const metricAdvice = advice[metric] || advice['clicks'];
  return isNegative ? metricAdvice.negative : metricAdvice.positive;
}

// Anomaly Dashboard - Customer feature for viewing and managing detected anomalies
export default function AnomalyDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pages, loading: loadingPages } = usePagesList();
  const selectedSite = useSiteStore(state => state.selectedSite);
  const [items, setItems] = useState<Anomaly[]>([]);
  const [stats, setStats] = useState<AnomalyStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read initial page_id from URL params (for deep linking from dashboard)
  const initialPageId = searchParams.get('page_id');
  
  // Filters
  const [pageId, setPageId] = useState<number | ''>(initialPageId ? Number(initialPageId) : '');
  const [metric, setMetric] = useState<string>('');
  const [severity, setSeverity] = useState<Anomaly['severity'] | ''>('');
  const [status, setStatus] = useState<Anomaly['status'] | ''>('');
  const [daysBack, setDaysBack] = useState<number>(7);

  const [selected, setSelected] = useState<Anomaly | null>(null);
  const debounceTimer = useRef<number | null>(null);

  const load = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      setError(null);
      const filters = {
        page: 1,
        page_size: 10,
        site_id: selectedSite.id,
        page_id: pageId === '' ? undefined : pageId,
        metric: metric || undefined,
        severity: (severity || undefined) as any,
        status: (status || undefined) as any,
        days_back: daysBack,
      };
      const [listRes, statsRes] = await Promise.all([
        fetchAnomalies(filters),
        fetchAnomalyStats({ site_id: selectedSite.id, days_back: daysBack }),
      ]);
      setItems(listRes.data.anomalies);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Sync URL params when pageId changes from URL (deep link)
  useEffect(() => {
    const urlPageId = searchParams.get('page_id');
    if (urlPageId && Number(urlPageId) !== pageId) {
      setPageId(Number(urlPageId));
    }
  }, [searchParams]);

  // Reset pageId when site changes or pages list changes
  useEffect(() => {
    // If current pageId is not in the new pages list, reset it
    if (pageId && !pages.find(p => p.id === pageId)) {
      setPageId('');
    }
  }, [selectedSite?.id, pages]);

  const handleAck = async (traceId: string) => {
    try {
      await acknowledgeAnomaly(traceId, 'Acknowledged from dashboard');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to acknowledge');
    }
  };

  const handleResolve = async (traceId: string) => {
    try {
      await resolveAnomaly(traceId, 'Resolved from dashboard', false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to resolve');
    }
  };

  // Auto-load stats when site changes
  useEffect(() => {
    if (selectedSite) {
      setLoading(true);
      fetchAnomalyStats({ site_id: selectedSite.id, days_back: daysBack })
        .then(res => setStats(res.data))
        .finally(() => setLoading(false));
    }
  }, [selectedSite, daysBack]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      <Box>
        <Typography variant="h5" gutterBottom>Anomaly Detection Dashboard</Typography>
        <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
          Is something broken or unusual happening recently?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Automatically detect unusual traffic patterns using Z-score statistical analysis (3σ threshold over 90-day baseline). 
          Catch SEO issues before they impact revenue.
        </Typography>
      </Box>

      {/* Help Banner */}
      <Paper elevation={0} sx={{ p: 2, backgroundColor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <InfoOutlined color="info" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>How to Use This Dashboard</Typography>
            <Typography variant="body2" component="div" sx={{ fontSize: '0.875rem' }}>
              <strong>1. Monitor Critical Alerts:</strong> Red banner shows urgent anomalies needing immediate action<br />
              <strong>2. Filter & Investigate:</strong> Use filters to focus on specific pages, metrics, or severity levels<br />
              <strong>3. Take Action:</strong> Click "Details" for root cause analysis, "Acknowledge" to track progress, "Resolve" when fixed<br />
              <strong>4. Learn Patterns:</strong> Review resolved anomalies to prevent future issues and replicate successes
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Critical Banner */}
      <CriticalAlertsBanner daysBack={daysBack} onClickFilterCritical={() => setSeverity('critical')} />

      {/* Filters */}
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Autocomplete
              size="small"
              sx={{ width: 280 }}
              options={pages}
              value={pages.find(p => p.id === pageId) || null}
              onChange={(_, newValue) => setPageId(newValue?.id || '')}
              getOptionLabel={(option) => option.label}
              loading={loadingPages}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page"
                  placeholder="All Pages"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Article sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Article fontSize="small" color="action" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.url.length > 40 ? '...' + option.url.slice(-40) : option.url}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <TextField
              label="Metric"
              select
              size="small"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="clicks">Clicks</MenuItem>
              <MenuItem value="impressions">Impressions</MenuItem>
              <MenuItem value="ctr">CTR (Click-Through Rate)</MenuItem>
              <MenuItem value="position">Position (Ranking)</MenuItem>
            </TextField>
            <TextField
              label="Severity"
              select
              size="small"
              value={severity}
              onChange={(e) => setSeverity((e.target.value as any) || '')}
              sx={{ width: 160 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </TextField>
            <TextField
              label="Status"
              select
              size="small"
              value={status}
              onChange={(e) => setStatus((e.target.value as any) || '')}
              sx={{ width: 180 }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="false_positive">False Positive</MenuItem>
            </TextField>
            <TextField
              label="Days Back"
              type="number"
              size="small"
              value={daysBack}
              onChange={(e) => setDaysBack(Math.max(1, Number(e.target.value) || 7))}
              sx={{ width: 130 }}
            />
            <Button 
              variant="contained" 
              onClick={load}
              disabled={!selectedSite || loading}
            >
              Load
            </Button>
            <Button variant="text" onClick={() => { setPageId(''); setMetric(''); setSeverity(''); setStatus(''); }}>Clear</Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1">
            7-Day Stats of {selectedSite?.name || 'All Sites'}
          </Typography>
          <Stack direction="row" spacing={2} mt={1}>
            <Chip label={`Total: ${stats?.total_anomalies ?? '-'}`} />
            <Chip label={`Critical: ${stats?.by_severity?.critical ?? '-'}`} color="error" />
            <Chip label={`High: ${stats?.by_severity?.high ?? '-'}`} color="warning" />
            <Chip label={`Active: ${stats?.by_status?.active ?? '-'}`} color="info" />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Recent Anomalies {items.length > 0 && `(${items.length})`}
            {(() => {
              const filters = [];
              if (pageId) {
                const page = pages.find(p => p.id === pageId);
                filters.push(`Page: ${page?.url || `ID ${pageId}`}`);
              }
              if (metric) {
                const metricNames: Record<string, string> = {
                  clicks: 'Clicks',
                  impressions: 'Impressions',
                  ctr: 'CTR',
                  position: 'Position'
                };
                filters.push(`Metric: ${metricNames[metric] || metric}`);
              }
              if (severity) filters.push(`Severity: ${severity}`);
              if (status) filters.push(`Status: ${status}`);
              filters.push(`Last ${daysBack} days`);
              return <Typography component="span" variant="body2" color="text.secondary"> for {filters.join(', ')}</Typography>;
            })()}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {error && (<Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>)}
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading…</Typography>
            </Stack>
          )}
          {!loading && items.length === 0 && (
            <Alert severity="success">
              <AlertTitle>No Anomalies Detected</AlertTitle>
              All metrics are within normal ranges. The anomaly detection job runs daily at 7:00 AM to analyze traffic patterns.
            </Alert>
          )}
          <Stack spacing={2}>
            {items.map((item) => (
              <Card key={item.trace_id} variant="outlined" sx={{ borderLeft: item.severity === 'critical' ? '4px solid' : undefined, borderLeftColor: 'error.main' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          {item.delta_percent < 0 ? (
                            <TrendingDown color="error" />
                          ) : (
                            <TrendingUp color="success" />
                          )}
                          <Typography variant="h6">
                            {item.metric.toUpperCase()}: {item.delta_percent > 0 ? '+' : ''}{item.delta_percent.toFixed(1)}% Change
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Page ID: {item.page_id} · Trace: {item.trace_id} · Detected: {new Date(item.detected_at).toLocaleString()}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={1} mb={1.5}>
                          <Chip label={item.severity.toUpperCase()} color={severityColor(item.severity)} size="small" />
                          <Chip label={item.status.replace('_', ' ').toUpperCase()} variant="outlined" size="small" />
                          <Chip label={`Confidence: ${(item.confidence * 100).toFixed(0)}%`} size="small" />
                        </Stack>
                        
                        {/* Possible Causes from Backend + Advice */}
                        <Alert severity={item.delta_percent < 0 ? "warning" : "info"} sx={{ mt: 1.5 }}>
                          <AlertTitle>Analysis & Recommended Action</AlertTitle>
                          {item.possible_causes && (
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                              📊 {item.possible_causes}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            💡 {getAnomalyAdvice(item.metric, item.delta_percent)}
                          </Typography>
                        </Alert>
                      </Box>
                      <Stack direction="row" spacing={1} ml={2}>
                        <Tooltip title="View detailed analysis and time series data">
                          <Button size="small" variant="outlined" onClick={() => setSelected(item)}>
                            Details
                          </Button>
                        </Tooltip>
                        <Tooltip title="Mark as acknowledged - you're investigating this issue">
                          <Button size="small" variant="outlined" onClick={() => handleAck(item.trace_id)} disabled={item.status !== 'active'}>
                            Acknowledge
                          </Button>
                        </Tooltip>
                        <Tooltip title="Mark as resolved - issue is fixed or false positive">
                          <Button size="small" variant="contained" onClick={() => handleResolve(item.trace_id)} disabled={item.status === 'resolved'}>
                            Resolve
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <AnomalyDetailDrawer
        open={!!selected}
        traceId={selected?.trace_id}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}

function severityColor(sev: Anomaly['severity']) {
  switch (sev) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'default';
  }
}
